/**
 * The work-time panel (plan 0026 P2, step 3): a row on Arbeidstidsoversikt opens this beside the
 * list, not a second profile. Three tabs: Oversikt (the period and today), Registreringer (the raw
 * records, each editable, and a manual one added), Eventuelt (what needs a manager, and the
 * record of changes). Editing and adding reuse the shift drawer, so the reason rule, the history
 * and the permission split are the same as everywhere else.
 */
import { type ApiShift, type ApiShiftType, type ApiUser, useWorkTimeChanges } from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { breakPolicy, oslo, worked, workedResult } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, Chip, Drawer } from '@/ui';
import { DN, fmtDs, fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { addDays, dow } from '@timefly/core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { type DraftShift, draftOf, newDraft } from '../vaktplan/ShiftDrawer';
import History from './History';
import { ISSUE_TEXT, type Row, hm } from './rows';

type Tab = 'oversikt' | 'registreringer' | 'eventuelt';
const TABS: { key: Tab; label: string }[] = [
  { key: 'oversikt', label: 'Oversikt' },
  { key: 'registreringer', label: 'Registreringer' },
  { key: 'eventuelt', label: 'Eventuelt' },
];

const dayOf = (s: ApiShift) => oslo(s.start_time)?.date ?? '';
const clock = (iso: string | null | undefined) => oslo(iso)?.time ?? '–';

export function WorkPanel({
  row,
  shifts,
  from,
  to,
  today,
  now,
  types,
  startTab,
  onClose,
  onEdit,
}: {
  row: Row | null;
  /** this person's shifts in the fetched window */
  shifts: ApiShift[];
  from: string;
  to: string;
  today: string;
  now: number;
  types: ApiShiftType[];
  startTab?: Tab;
  onClose: () => void;
  /** opens the shift drawer on an existing shift or a new registration */
  onEdit: (d: DraftShift) => void;
}) {
  const can = useCan();
  const { tenant } = useTenant();
  const policy = breakPolicy(tenant);
  const [tab, setTab] = useState<Tab>(startTab ?? 'oversikt');
  const u = row?.u;
  const historyQ = useWorkTimeChanges({ user_id: u?.id }, !!u && can('timeliste.se') && tab === 'eventuelt');
  if (!row || !u) return null;
  const mayAdd = can('timeliste.rette');
  const inPeriod = shifts.filter((s) => dayOf(s) >= from && dayOf(s) <= to);
  const days: string[] = [];
  for (let d = from; d <= to && days.length < 31; d = addDays(d, 1)) days.push(d);
  const hoursOf = (s: ApiShift) =>
    s.id === row.duty?.id
      ? Math.max(0, (now - Date.parse(s.punch_in_time ?? '')) / 3600_000)
      : (worked(s, policy) ?? 0);
  const pause = (s: ApiShift) => {
    const r = workedResult(s, policy);
    return r.status === 'ok' ? `${Math.round(r.deductedMinutes)} min` : '–';
  };
  const unplanned = types.find((t) => /uten plan/i.test(t.name ?? '')) ?? types[0];
  const add = (date: string) => onEdit({ ...newDraft(date, u.id, unplanned), published: true });
  const todays = shifts.filter((s) => dayOf(s) === today);
  const withRegistration = new Set(inPeriod.filter((s) => s.punch_in_time).map(dayOf)).size;

  const dayLine = (d: string) => {
    const ss = inPeriod.filter((s) => dayOf(s) === d);
    return (
      <div key={d} className="wprow" data-testid="panel-day">
        <span className="wpd">
          {DN[dow(d)]} {fmtDs(d)}
        </span>
        {ss.length ? (
          <span className="wpv">
            {ss.map((s) => (
              <button key={s.id} type="button" className="lnk mono" onClick={() => onEdit(draftOf(s))}>
                {s.punch_in_time
                  ? `${clock(s.punch_in_time)}–${s.punch_out_time ? clock(s.punch_out_time) : s.id === row.duty?.id ? T('nå') : '?'}`
                  : T('ikke stemplet')}
                {s.punch_in_time ? `  ${hm(hoursOf(s))}` : ''}
              </button>
            ))}
          </span>
        ) : (
          <span className="wpv sub">–</span>
        )}
        {mayAdd && (
          <button
            type="button"
            className="lnk"
            aria-label={`${T('Legg til registrering')} ${fmtDs(d)}`}
            onClick={() => add(d)}
          >
            +
          </button>
        )}
      </div>
    );
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={fullName(u)}
      sub={
        <>
          {row.duty ? (
            <Chip tone="ok" small>
              ● {T('På jobb nå')}, {T('startet')} {clock(row.duty.punch_in_time)}
            </Chip>
          ) : (
            T('Ikke på jobb')
          )}{' '}
          ,{' '}
          <Link to={`/ansatte/${u.id}`}>
            <Icon name="user" />
            {T('Åpne ansatt')}
          </Link>
        </>
      }
    >
      <div className="tabs" role="tablist">
        {TABS.map((x) => (
          <button
            key={x.key}
            type="button"
            role="tab"
            aria-selected={tab === x.key}
            className={tab === x.key ? 'on' : ''}
            onClick={() => setTab(x.key)}
          >
            {T(x.label)}
            {x.key === 'eventuelt' && row.issues.length > 0 ? ` (${row.issues.length})` : ''}
          </button>
        ))}
      </div>

      {tab === 'oversikt' && (
        <>
          <div className="kpi" style={{ marginTop: 12 }}>
            <div>
              <b>{hm(row.period)}</b>
              <span>{T('Registrert i perioden')}</span>
            </div>
            <div>
              <b>{withRegistration}</b>
              <span>{T('dager med registrering')}</span>
            </div>
            {u.max_hours_per_week != null && u.max_hours_per_week > 0 && (
              <div>
                <b>{u.max_hours_per_week} t</b>
                <span>{T('avtalt per uke')}</span>
              </div>
            )}
          </div>
          <h4>{T('Dagens registrering')}</h4>
          {todays.length ? (
            todays.map((s) => (
              <div key={s.id} className="two mono" style={{ marginBottom: 8 }}>
                <span>
                  {T('Inn')} {clock(s.punch_in_time)}
                </span>
                <span>
                  {T('Ut')} {s.punch_out_time ? clock(s.punch_out_time) : '–'}
                </span>
                <span>
                  {T('Pause')} {pause(s)}
                </span>
                <b>{hm(hoursOf(s))}</b>
              </div>
            ))
          ) : (
            <p className="sub">{T('Ingen registrering i dag.')}</p>
          )}
          <h4>{T('Registreringer i perioden')}</h4>
          {days.map(dayLine)}
          <p className="hint">
            {T('En dag uten registrering er bare tom. Den er ikke et avvik.')}{' '}
            <Link to={`/timeliste?mnd=${from.slice(0, 7)}`}>{T('Se detaljert timeliste')} →</Link>
          </p>
        </>
      )}

      {tab === 'registreringer' && (
        <>
          {mayAdd && (
            <Button tone="pri" onClick={() => add(today)} style={{ marginTop: 12 }}>
              + {T('Legg til registrering')}
            </Button>
          )}
          <table className="t" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>{T('Dag')}</th>
                <th>{T('Inn')}</th>
                <th>{T('Ut')}</th>
                <th>{T('Pause')}</th>
                <th className="n">{T('Total')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {inPeriod
                .slice()
                .sort((a, b) => a.start_time.localeCompare(b.start_time))
                .map((s) => (
                  <tr key={s.id} data-testid="panel-registration">
                    <td>
                      {DN[dow(dayOf(s))]} {fmtDs(dayOf(s))}
                      {s.is_approved ? ' ✓' : ''}
                    </td>
                    <td className="mono">{clock(s.punch_in_time)}</td>
                    <td className="mono">
                      {s.punch_out_time ? clock(s.punch_out_time) : s.id === row.duty?.id ? T('nå') : '–'}
                    </td>
                    <td>{pause(s)}</td>
                    <td className="n mono">{s.punch_in_time ? hm(hoursOf(s)) : '–'}</td>
                    <td>
                      <Button small onClick={() => onEdit(draftOf(s))}>
                        {T('Rediger')}
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!inPeriod.length && <p className="sub">{T('Ingen registreringer i perioden.')}</p>}
        </>
      )}

      {tab === 'eventuelt' && (
        <>
          <h4>{T('Avvik og merknader')}</h4>
          {row.issues.length ? (
            row.issues.map(({ issue, shift }) => (
              <div key={shift.id} className="wprow" data-testid="panel-issue">
                <Chip tone={issue === 'deviation' ? 'warn' : 'dgr'} small>
                  {T(ISSUE_TEXT[issue])}
                </Chip>
                <span className="wpv">
                  {DN[dow(dayOf(shift))]} {fmtDs(dayOf(shift))}, {clock(shift.punch_in_time)}–
                  {shift.punch_out_time ? clock(shift.punch_out_time) : '–'}
                </span>
                <Button small onClick={() => onEdit(draftOf(shift))}>
                  {issue === 'open' ? T('Lukk registrering') : T('Rett')}
                </Button>
              </div>
            ))
          ) : (
            <p className="sub">{T('Ingenting å kontrollere i perioden.')}</p>
          )}
          <h4>{T('Endringer')}</h4>
          <History
            rows={(historyQ.data ?? []).filter((c) => c.created_at.slice(0, 10) >= addDays(from, -1))}
            empty={T('Ingen manuelle endringer i perioden.')}
          />
        </>
      )}
    </Drawer>
  );
}

export type { Tab as PanelTab };
export const personShifts = (shifts: ApiShift[], u: ApiUser) => shifts.filter((s) => s.user_id === u.id);
